import type { SettingsTemplateModel } from '@/shared/generated'

import { useCallback } from 'react'
import { entries, forEach, isPlainObject, pick, pipe } from 'lodash/fp'
import { EllipsisVerticalIcon, PlusIcon, PuzzleIcon } from 'lucide-react'
import { Slot } from 'radix-ui'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useCreateSettingsTemplateMutation } from '@/entities/settings-template'

import { Button } from '@/shared/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { queryClient } from '@/shared/lib/query-client'
import { processingMessage } from '@/shared/tools/message'

import { getSettingConfig } from '../lib/getSettingConfig'

export function SettingTabActions({ setting }: { setting: string }) {
	const { t } = useTranslation()

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button size='icon-sm' variant='ghost' aria-label='Tab actions'>
					<EllipsisVerticalIcon />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align='end' side='bottom'>
				<CreateSettingsTemplate setting={setting}>
					<DropdownMenuItem>
						<PlusIcon />
						{t('settings.action.save_as_template')}
					</DropdownMenuItem>
				</CreateSettingsTemplate>
				<UseSettingsTemplate setting={setting}>
					<DropdownMenuItem>
						<PuzzleIcon />
						{t('settings.action.use_template')}
					</DropdownMenuItem>
				</UseSettingsTemplate>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
const CreateSettingsTemplate = ({
	setting,
	children,
}: {
	setting: string
	children: React.ReactNode
}) => {
	const { t } = useTranslation()

	const { watch } = useFormContext()
	const { name, getTemplateType } = getSettingConfig(setting)
	const createTemplateMutation = useCreateSettingsTemplateMutation({
		onSuccess() {
			queryClient.invalidateQueries({ queryKey: ['settings-templates'] })
		},
	})
	const handleSaveAsTemplate = useCallback(async () => {
		ampli.settingsDropdownSaveSettingAsTemplate()
		const formValues = watch()
		const settingValue = formValues.body.settings[name]
		const engine = settingValue?.engine

		const payload = {
			name: formValues.name,
			description: formValues.description,
			template_type: getTemplateType(engine),
			body: pick([name], formValues.body.settings),
		} as SettingsTemplateModel

		toast.promise(createTemplateMutation.mutateAsync(payload), {
			loading: processingMessage(t),
			success: t('template_saved_successfully'),
			error: t('fatal_error_title'),
		})
	}, [createTemplateMutation])

	return <Slot.Root onClick={handleSaveAsTemplate}>{children}</Slot.Root>
}
const UseSettingsTemplate = ({ setting, children }: any) => {
	const { showModal } = useModals()
	const { setValue } = useFormContext()

	const { name, template_types } = getSettingConfig(setting)

	const handleApply = (template: SettingsTemplateModel) => {
		const value = template.body[name]
		const path = `body.settings.${name}`
		/**
		 * Notify granular subscribers (e.g. useFieldArray) by setting keys individually
		 * to prevent stale UI state often caused by bulk setValue on parent objects.
		 */
		if (isPlainObject(value)) {
			pipe(
				entries,
				forEach(([key, val]) => {
					setValue(`${path}.${key}`, val, {
						shouldDirty: true,
					})
				})
			)(value)
		} else {
			setValue(path, value, {
				shouldDirty: true,
			})
		}
	}

	const handleUseTemplate = () => {
		ampli.settingsDropdownApplyTemplate()
		showModal('SETTING_TEMPLATES_MODAL', {
			template_types,
			handleApply,
		})
	}

	return <Slot.Root onClick={handleUseTemplate}>{children}</Slot.Root>
}
